import { type AllOrNothing } from '@src/types/utils';
import { Button } from '@components/Button.tsx';
import { ButtonType } from '@common/types/button';
import {
  type RequiredSortingPaginatedTableProps,
  type SelectionProps,
} from '@components/table/types';
import { classNames } from '@src/utils/classNames';
import { useTranslation } from 'react-i18next';
import PaginationPanel from '@components/table/PaginationPanel.tsx';
import TableBody from '@components/table/TableBody.tsx';
import TableHead from '@components/table/TableHead.tsx';

export type SortingPaginatedTableProps<
  T,
  AdditionalProps extends Record<string, unknown> = Record<string, never>,
> = RequiredSortingPaginatedTableProps<T, AdditionalProps> &
  AllOrNothing<SelectionProps> & { embedded?: boolean };

export default function SortingPaginatedTable<
  T,
  AdditionalProps extends Record<string, unknown> = Record<string, never>,
>({
  topHeader,
  currentPage,
  numberOfAvailablePages,
  setCurrentPage,
  sort,
  setSort,
  loading,
  data,
  columns,
  getId,
  totalCount,
  hideResultCounter = false,
  className,
  itemsPerPage,
  additionalProps,
  onRowClick,
  getRowLink,
  embedded,
  ...rest
}: SortingPaginatedTableProps<T, AdditionalProps>) {
  const { t } = useTranslation('table');

  const isPaginated =
    currentPage !== undefined &&
    numberOfAvailablePages !== undefined &&
    numberOfAvailablePages > 1 &&
    setCurrentPage !== undefined;

  return (
    <div
      className={classNames(
        'w-full drop-shadow-table flex flex-col min-h-0 h-full',
        className,
      )}
    >
      <div
        className={classNames(
          'min-w-full align-middle bg-white flex flex-col min-h-0 h-full',
          !embedded && 'border border-blue-100 rounded-xl px-4 py-6',
        )}
      >
        {(topHeader || !hideResultCounter) && (
          <div>
            <div>
              {topHeader ? <div className="pt-4 px-6">{topHeader}</div> : <></>}
            </div>
            {!hideResultCounter &&
              (!rest.selectedIds || rest.selectedIds.size === 0) && (
                <div className={classNames('py-4 px-6')}>
                  <p className="font-bold">
                    {t('totalResults', {
                      total: totalCount,
                    })}
                  </p>
                </div>
              )}
          </div>
        )}
        {rest.selectedIds && rest.selectedIds.size > 0 && (
          <div className={'py-4 px-6 flex justify-between items-center'}>
            <p className="font-bold">
              {t('selected', {
                selected: rest.selectedIds.size,
                total: totalCount,
              })}
            </p>
            <div>
              <Button
                label={t('deselectAll')}
                buttonType={ButtonType.SECONDARY}
                onClick={() => rest.setSelectedIds(new Set())}
              />
              {rest.selectedActions}
            </div>
          </div>
        )}
        <div className="relative flex flex-col min-h-0 overflow-auto h-full">
          <table className="min-w-full">
            <TableHead
              columns={columns}
              totalCount={totalCount}
              sort={sort}
              setSort={setSort}
              data={data}
              getId={getId}
              {...rest}
            />
            <TableBody
              columns={columns}
              data={data}
              getId={getId}
              itemsPerPage={itemsPerPage}
              loading={loading}
              onRowClick={onRowClick}
              getRowLink={getRowLink}
              additionalProps={additionalProps}
              {...rest}
            />
          </table>
          {isPaginated && (
            <PaginationPanel
              currentPage={currentPage + 1}
              numberOfAvailablePages={numberOfAvailablePages}
              setNewPage={(newPage) => setCurrentPage(newPage - 1)}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}
